import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { Heart, MapPin, Navigation, Store } from 'lucide-react';
import { TOGGLE_FAVORITE_STORE } from '@/graphql/mutations';
import { useAuth } from '@/context/AuthContext';
import { trackClick } from '@/services/tracking';
import { trackFavorite, trackGetDirections } from '@/lib/analytics';
import { mapsPlaceUrl } from '@/utils/directions';
import { cityLabel, displayName } from '@/seo/text';
import { entityPath } from '@/seo/urls';
import { Img } from '@/components/ui';
import type { Store as StoreEntity, StoreMinimal } from '@/types';

const STORE_THUMB = { width: 128, fit: 'cover' } as const;

interface StoreListItemProps {
  store: StoreEntity | StoreMinimal;
  /**
   * The city chip's label. A list grouped by city passes its heading's label,
   * so every row under "DHA Lahore (2)" reads "DHA Lahore" however the row
   * itself typed it ("dha lahore"); null shows no chip. Default:
   * `cityLabel(store.city)`.
   */
  city?: string | null;
}

/** `location` is stored as a "lat,long" coordinate pair; turn it into a Google
 *  Maps link. Returns null for anything that isn't a coordinate pair. */
function mapsUrl(location?: string): string | null {
  const trimmed = location?.trim();
  if (!trimmed || !/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(trimmed)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
}

/**
 * A single store rendered as a horizontal list row — small logo, then the
 * name / address / city, with an optional map link. Replaces the old logo-heavy
 * card in the brand-detail Stores section (chain stores share one logo, so the
 * cards were indistinguishable; the details are what matter here).
 */
export function StoreListItem({ store, city: groupCity }: StoreListItemProps) {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { token } = useAuth();
  const [favorite, setFavorite] = useState(!!store.isFavorite);
  const [toggleFavorite] = useMutation(TOGGLE_FAVORITE_STORE);

  useEffect(() => {
    setFavorite(!!store.isFavorite);
  }, [store.isFavorite]);

  const address = (store as StoreEntity).address?.trim();
  // The group heading's label when a list passes one; otherwise the row's own
  // city through the same helper (an all-lowercase "lahore" reads "Lahore").
  const city = groupCity === undefined ? cityLabel((store as StoreEntity).city) : (groupCity ?? '');
  // Names pasted from social posts (𝗯𝗼𝗹𝗱 letters, emoji, hashtags) are shown
  // and announced cleaned; the maps link below still searches the stored name.
  const name = displayName(store.name);
  // Build the Google Maps link from the store's place ID (opens the place page
  // with details); fall back to a coordinate search URL built from `location`.
  const placeId = (store as StoreEntity).googleMapsPlaceID?.trim();
  const directions = placeId
    ? mapsPlaceUrl(placeId, { location: store.location, label: (store as StoreEntity).name })
    : mapsUrl(store.location);

  const handleClick = () => trackClick('store', store.id);

  // No hover prefetch of the store page (the brand, shop and promotion cards
  // keep theirs): STORE_BY_ID carries the brand's whole store list and offers,
  // ~43 KB for a 169-store brand, and these rows fill brand pages, so a pointer
  // moving down the list would fetch one of those per row it rests on — and
  // the page refetches it anyway (cache-and-network). A lighter query would
  // not help either: the page paints nothing from an incomplete cache entry.

  const handleHeart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      navigate('/login');
      return;
    }
    const next = !favorite;
    const cacheId = client.cache.identify({
      __typename: (store as { __typename?: string }).__typename ?? 'Store',
      id: store.id,
    });
    setFavorite(next);
    toggleFavorite({
      variables: { id: store.id },
      update: (cache) => {
        cache.modify({ id: cacheId, fields: { isFavorite: () => next } });
      },
    })
      .then(() => {
        if (next) trackFavorite({ contentType: 'store', itemId: store.id });
      })
      .catch(() => {
        setFavorite(!next);
        client.cache.modify({ id: cacheId, fields: { isFavorite: () => !next } });
      });
  };

  return (
    <div className="group flex items-center gap-3 rounded-2xl border border-card-border bg-card p-3 transition-all duration-200 hover:border-primary/40 hover:shadow-md sm:gap-4 sm:p-4">
      {/* Main clickable area → store detail. Kept separate from the action
          buttons so we don't nest interactive elements inside the link. */}
      <Link
        to={entityPath(store, 'store')}
        onClick={handleClick}
        className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4"
      >
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-surface sm:h-16 sm:w-16">
          <Img
            media={store.logo}
            // One small `src`, no per-row srcset: a brand page holds hundreds of
            // these rows (audit F1) and the thumbnail is at most 64 CSS px.
            transform={STORE_THUMB}
            // Decorative: the name is the link's text right beside it, so an alt
            // would only make screen readers say it twice (image-redundant-alt).
            alt=""
            className="h-full w-full object-cover"
            fallback={<Store size={24} className="text-ink-tertiary" />}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-ink transition-colors group-hover:text-primary-strong sm:text-base">
              {name}
            </p>
            {city && (
              <span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium text-ink-secondary">
                {city}
              </span>
            )}
          </div>
          {address && (
            <p className="mt-1 flex items-start gap-1.5 text-xs text-ink-secondary sm:text-sm">
              <MapPin size={14} className="mt-0.5 shrink-0 text-ink-tertiary" />
              <span className="line-clamp-2">{address}</span>
            </p>
          )}
        </div>
      </Link>

      {/* Actions — siblings of the link, not nested inside it. */}
      <div className="flex shrink-0 flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={handleHeart}
          // Named after the store: a brand page lists many of these rows, and a
          // run of identical "Add to favorites" buttons is useless to a screen reader.
          aria-label={
            favorite ? `Remove ${name} from favorites` : `Add ${name} to favorites`
          }
          className="rounded-full p-1.5 transition-colors hover:bg-surface"
        >
          <Heart
            size={18}
            fill={favorite ? 'currentColor' : 'none'}
            className={favorite ? 'text-error' : 'text-ink-tertiary'}
            aria-hidden="true"
          />
        </button>
        {directions && (
          <a
            href={directions}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackGetDirections({ contentType: 'store', itemId: store.id })}
            aria-label={`Open ${name} in maps`}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-primary-strong transition-colors hover:bg-primary/10"
          >
            <Navigation size={12} />
            Map
          </a>
        )}
      </div>
    </div>
  );
}

export default StoreListItem;
